import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2,
  XCircle,
  ArrowRight,
  TrendingUp,
  Brain,
  ShoppingCart,
  Users,
  Repeat,
  DollarSign,
  Clock,
  Zap,
  Building2,
  Mail
} from 'lucide-react';

/**
 * Enterprise Explainer
 * 
 * 1-page operational clarity for B2B buyers.
 * Not marketing fluff — answers the real questions.
 */

const EnterprisePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background" data-testid="enterprise-page">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Badge className="bg-primary/20 text-primary-foreground mb-4">
            For Grocery Partners
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold font-['Playfair_Display'] mb-4">
            Carted for Enterprise
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl">
            Meal planning infrastructure that increases basket size and customer retention. 
            White-label ready.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12 space-y-16">
        
        {/* SECTION 1: What It Does */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Zap className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">What Carted Does</h2>
          </div>
          
          <div className="bg-slate-50 rounded-xl p-6 space-y-4">
            <p className="text-lg">
              Carted is <strong>meal planning infrastructure</strong> that sits between recipe discovery 
              and grocery checkout.
            </p>
            
            <div className="grid md:grid-cols-3 gap-4 mt-6">
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <Brain className="h-8 w-8 text-primary mb-3" />
                  <h3 className="font-semibold mb-2">Generates Meal Plans</h3>
                  <p className="text-sm text-muted-foreground">
                    AI-powered weekly plans based on preferences, dietary needs, and budget
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <ShoppingCart className="h-8 w-8 text-primary mb-3" />
                  <h3 className="font-semibold mb-2">Builds Smart Carts</h3>
                  <p className="text-sm text-muted-foreground">
                    Normalized ingredient lists with deduplication, unit conversion, and store mapping
                  </p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <Repeat className="h-8 w-8 text-primary mb-3" />
                  <h3 className="font-semibold mb-2">Drives Repeat Visits</h3>
                  <p className="text-sm text-muted-foreground">
                    Weekly planning cadence creates habitual engagement with your platform
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* SECTION 2: What It Replaces */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Brain className="h-5 w-5 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold">What It Replaces</h2>
          </div>
          
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold mb-4">Decision Fatigue</h3>
              <p className="text-muted-foreground mb-6">
                The average household makes <strong>21+ meal decisions per week</strong>. 
                This cognitive load leads to:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-4 bg-white rounded-lg">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Impulse purchases</p>
                    <p className="text-sm text-muted-foreground">23% of grocery spend is unplanned</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-lg">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Multiple trips</p>
                    <p className="text-sm text-muted-foreground">2.3 trips/week average (forgotten items)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-lg">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Takeout default</p>
                    <p className="text-sm text-muted-foreground">&quot;What&apos;s for dinner?&quot; ends in delivery</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-lg">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Food waste</p>
                    <p className="text-sm text-muted-foreground">31% of purchased groceries are thrown away</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-primary/10 rounded-lg">
                <p className="text-sm">
                  <strong>Carted eliminates this.</strong> One decision (&quot;plan my week&quot;) replaces 21 daily decisions.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* SECTION 3: What It Increases */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold">What It Increases</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <DollarSign className="h-8 w-8 text-green-600" />
                  <div>
                    <h3 className="text-2xl font-bold text-green-600">+18%</h3>
                    <p className="text-sm text-muted-foreground">Average Basket Size</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Planned shopping = intentional purchases. Customers buy more items per trip 
                  because they&apos;re cooking more meals at home.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="h-8 w-8 text-green-600" />
                  <div>
                    <h3 className="text-2xl font-bold text-green-600">+47%</h3>
                    <p className="text-sm text-muted-foreground">Customer Retention</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Weekly meal planning creates a habit loop. Customers return every Sunday 
                  to plan, then shop throughout the week.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="h-8 w-8 text-green-600" />
                  <div>
                    <h3 className="text-2xl font-bold text-green-600">3.2x</h3>
                    <p className="text-sm text-muted-foreground">Session Duration</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Planning a week of meals takes 5-10 minutes. That&apos;s engagement time 
                  competitors don&apos;t get.
                </p>
              </CardContent>
            </Card>
            
            <Card className="border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <Repeat className="h-8 w-8 text-green-600" />
                  <div>
                    <h3 className="text-2xl font-bold text-green-600">Weekly</h3>
                    <p className="text-sm text-muted-foreground">Return Cadence</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Meal planning is inherently weekly. You&apos;re not competing for &quot;when they 
                  need something&quot; — you own their Sunday routine.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* SECTION 4: What It Does NOT Do */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
              <XCircle className="h-5 w-5 text-slate-600" />
            </div>
            <h2 className="text-2xl font-bold">What It Does NOT Do</h2>
          </div>
          
          <Card className="bg-slate-50">
            <CardContent className="pt-6">
              <p className="text-muted-foreground mb-6">
                Clear boundaries help you understand where Carted fits.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
                  <XCircle className="h-6 w-6 text-slate-400 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">Not a meal kit service</h4>
                    <p className="text-sm text-muted-foreground">
                      We don&apos;t ship food. Customers buy from your stores, at your prices, with your margins.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
                  <XCircle className="h-6 w-6 text-slate-400 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">Not a recipe site</h4>
                    <p className="text-sm text-muted-foreground">
                      We aggregate recipes, but the value is the planning engine and cart optimization — not content.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
                  <XCircle className="h-6 w-6 text-slate-400 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">Not a diet app</h4>
                    <p className="text-sm text-muted-foreground">
                      No calorie counting, no macro tracking. We use &quot;brand-safe&quot; labels like 
                      &quot;High Protein&quot; or &quot;Quick Meal&quot; — never medical claims.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-white rounded-lg border">
                  <XCircle className="h-6 w-6 text-slate-400 mt-0.5" />
                  <div>
                    <h4 className="font-semibold">Not competing with your e-commerce</h4>
                    <p className="text-sm text-muted-foreground">
                      Carted feeds into your checkout, not around it. Every cart we build ends at your register.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Integration Section */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold">Integration Options</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <Badge className="mb-3">Fastest</Badge>
                <h3 className="font-semibold mb-2">Affiliate Link</h3>
                <p className="text-sm text-muted-foreground">
                  Carted sends customers to your e-commerce with pre-filled carts. 
                  No dev work required.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <Badge variant="secondary" className="mb-3">Recommended</Badge>
                <h3 className="font-semibold mb-2">White-Label Embed</h3>
                <p className="text-sm text-muted-foreground">
                  Your branding, your domain. Carted powers the meal planning; 
                  customers never leave your site.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <Badge variant="outline" className="mb-3">Enterprise</Badge>
                <h3 className="font-semibold mb-2">API Integration</h3>
                <p className="text-sm text-muted-foreground">
                  Full API access. Build meal planning into your native app 
                  with real-time inventory and pricing.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-8">
          <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white border-0">
            <CardContent className="py-10">
              <h3 className="text-2xl font-bold mb-3">Ready to explore?</h3>
              <p className="text-slate-300 mb-6 max-w-md mx-auto">
                See a working demo or schedule a call to discuss your specific use case.
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Button 
                  size="lg" 
                  variant="secondary"
                  onClick={() => navigate('/demo')}
                  data-testid="enterprise-demo-btn"
                >
                  See Live Demo
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => window.location.href = 'mailto:enterprise@carted.app'}
                  data-testid="enterprise-contact-btn"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Contact Sales
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Footer note */}
        <div className="text-center text-sm text-muted-foreground pb-8">
          <p>Metrics based on internal data and industry benchmarks. Results may vary by implementation.</p>
        </div>
      </div>
    </div>
  );
};

export default EnterprisePage;
